"use client";

import { Helmet, HelmetProvider } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  lang?: string;
  noindex?: boolean;
}

const SEO = ({
  title = "Dumitrescu Photography | Professional Editorial & Architectural Photography",
  description = "Award-winning professional photographer specializing in editorial, architectural, portrait, and artistic photography in Sofia, Bulgaria.",
  keywords = "professional photographer, editorial photography, architectural photography, portrait photography, Sofia photographer, Bulgaria, fine art photography",
  image = "https://dumitrescuphotography.com/og-image.jpg",
  url = "https://dumitrescuphotography.com/",
  type = "website",
  lang = "en",
  noindex = false,
}: SEOProps) => {
  const { language } = useLanguage();
  
  const currentTitle = title;
  const currentDescription = description;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{currentTitle}</title>
      <meta name="description" content={currentDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Dumitrescu Photography" />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph */}
      <meta property="og:title" content={currentTitle} />
      <meta property="og:description" content={currentDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Dumitrescu Photography" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Dumitrescu Photography Portfolio" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentTitle} />
      <meta name="twitter:description" content={currentDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional Meta */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      
      {/* Structured Data - Only English version since multilingual content is client-side */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Dumitrescu Photography",
          "description": currentDescription,
          "url": url,
          "inLanguage": "en",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Sofia",
            "addressCountry": "BG"
          },
          "areaServed": ["RO", "BG", "International"],
          "serviceType": "Photography Services"
        })}
      </script>
    </Helmet>
  );
};

const SEOProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <HelmetProvider>
      {children}
    </HelmetProvider>
  );
};

export { SEO, SEOProvider };